import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ISubscription } from 'rxjs/Subscription';
import { ManuscriptsService } from '../../../services/manuscripts.service';
import { VocsService } from '../../../services/vocs.service';
import { NameListsService } from '../../../services/name-lists.service';
import { MssListFilters } from '../../../models/mss-list-filters';
import { VocTerm, VocTerms } from '../../../models/voc-term';
import { DateRange, DateCalendar } from '../../../models/date-range';
import { NameElement, NameList, NameTypeField, NAME_TYPES, NameListFilters } from '../../../models/name-list';
import { SifaSelection } from '../../../models/sifa-selection';

export class MssFiltersOrderBy {
    copyists: NameTypeField = null;
    patrons: NameTypeField = null;
    owners: NameTypeField = null;
}

export class MssFiltersContainsText {
    copyists: '';
    patrons: '';
    owners: '';
}

export interface DateCalendarOption {
    key: DateCalendar;
    label: string;
}

@Component({
  selector: 'app-mss-filters',
  templateUrl: './mss-filters.component.html',
  styleUrls: ['./mss-filters.component.css']
})
export class MssFiltersComponent implements OnInit, OnDestroy {
    private subscriptions: Array<ISubscription> = [];
    mssListFilters: MssListFilters;
    msTypes: VocTerms;
    countries: VocTerms;
    cities: VocTerms;
    libraries: VocTerms;
    copyDate: DateRange;
    copyists: NameList;
    copyPlaces: VocTerms;
    patrons: NameList;
    owners: NameList;
    sifaSections: VocTerms;
    sifaParts: VocTerms;
    sifaTreatises: VocTerms;
    sifaChapters: VocTerms;
    sifaOtherElements: VocTerms;
    sifaSelection: SifaSelection;
    nameTypes = NAME_TYPES;
    copyistFilters: NameListFilters;
    patronFilters: NameListFilters;
    ownerFilters: NameListFilters;
    dateCalendars: Array<DateCalendarOption> = [];
    libraryByCity: null;

    /* Filter elements */
    @ViewChild('msTypeFilter') msTypeFilterGroup: ElementRef;
    @ViewChild('countryFilter') countryFilterGroup: ElementRef;
    @ViewChild('cityFilter') cityFilterGroup: ElementRef;
    @ViewChild('libraryFilter') libraryFilterGroup: ElementRef;
    @ViewChild('placeFilter') placeFilterGroup: ElementRef;
    @ViewChild('dateFilter') dateFilterGroup: ElementRef;
    @ViewChild('copyistsFilter') copyistsFilterGroup: ElementRef;
    @ViewChild('patronsFilter') patronsFilterGroup: ElementRef;
    @ViewChild('ownersFilter') ownersFilterGroup: ElementRef;
    @ViewChild('sifaFilter') sifaFilterGroup: ElementRef;

    constructor(
        private manuscriptsService: ManuscriptsService,
        private vocsService: VocsService,
        private namelistsService: NameListsService) {
    }

    ngOnInit(): void {
        this.copyDate = {from: null, to: null};
        this.sifaSelection = new SifaSelection();
        this.copyistFilters = new NameListFilters();
        this.patronFilters = new NameListFilters();
        this.ownerFilters = new NameListFilters();

        this.subscriptions.push(
            /* Load filtering values */
            this.vocsService.getMsTypes().subscribe(response => this.msTypes = response, err => console.error(err)),
            this.vocsService.getCountries().subscribe(response => this.countries = response, err => console.error(err)),
            this.vocsService.getCities().subscribe(response => this.cities = response, err => console.error(err)),
            this.vocsService.getLibraries().subscribe(response => this.libraries = response, err => console.error(err)),
            this.vocsService.getMsCopyPlaces().subscribe(response => this.copyPlaces = response, err => console.error(err)),
            this.vocsService.getSifaSections().subscribe(response => this.sifaSections = response, err => console.error(err)),
            this.vocsService.getSifaParts().subscribe(response => this.sifaParts = response, err => console.error(err)),
            this.vocsService.getSifaTreatises().subscribe(response => this.sifaTreatises = response, err => console.error(err)),
            this.vocsService.getSifaChapters().subscribe(response => this.sifaChapters = response, err => console.error(err)),
            this.vocsService.getSifaOtherElements().subscribe(response => this.sifaOtherElements = response, err => console.error(err)),

            this.manuscriptsService.getMssListFilters().subscribe(
                response => {
                    this.mssListFilters = response;
                    if (this.mssListFilters.msType) {
                        this.expandFilterElement(this.msTypeFilterGroup);
                    }
                    if (this.mssListFilters.country) {
                        this.expandFilterElement(this.countryFilterGroup);
                    }
                    if (this.mssListFilters.city) {
                        this.expandFilterElement(this.cityFilterGroup);
                    }
                    if (this.mssListFilters.library) {
                        this.expandFilterElement(this.libraryFilterGroup);
                    }
                    if (this.mssListFilters.copyPlace) {
                        this.expandFilterElement(this.placeFilterGroup);
                    }
                    if (this.mssListFilters.copyDate) {
                        this.expandFilterElement(this.dateFilterGroup);
                        this.copyDate = this.mssListFilters.copyDate;
                    }
                    if (this.mssListFilters.copyistFilters) {
                        if (!this.mssListFilters.copyistFilters.isNull()) {
                            this.expandFilterElement(this.copyistsFilterGroup);
                        }
                        this.copyistFilters = this.mssListFilters.copyistFilters;
                    }
                    if (this.mssListFilters.patronFilters) {
                        if (!this.mssListFilters.patronFilters.isNull()) {
                            this.expandFilterElement(this.patronsFilterGroup);
                        }
                        this.patronFilters = this.mssListFilters.patronFilters;
                    }
                    if (this.mssListFilters.ownerFilters) {
                        if (!this.mssListFilters.ownerFilters.isNull()) {
                            this.expandFilterElement(this.ownersFilterGroup);
                        }
                        this.ownerFilters = this.mssListFilters.ownerFilters;
                    }
                    if (this.mssListFilters.sifaSelection) {
                        if (!this.mssListFilters.sifaSelection.isNull()) {
                            this.expandFilterElement(this.sifaFilterGroup);
                        }
                        this.sifaSelection = this.mssListFilters.sifaSelection;
                    }

                    /* Loading name list data */
                    this.sortCopyists();
                    this.sortPatrons();
                    this.sortOwners();
                },
                err => console.error(err)
            )
        );

        this.dateCalendars.push({key: DateCalendar.Islamic, label: 'Islamic'});
        this.dateCalendars.push({key: DateCalendar.Gregorian, label: 'Gregorian'});
    }

    addMsType(msType: VocTerm): void {
        this.removeAppFilters();
        this.mssListFilters.msType = msType;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addCountry(country: VocTerm): void {
        this.removeAppFilters();
        this.mssListFilters.country = country;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addCity(city: VocTerm): void {
        this.removeAppFilters();
        this.mssListFilters.city = city;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addLibrary(library: VocTerm): void {
        this.removeAppFilters();
        this.mssListFilters.library = library;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addCopyDate(copyDate: DateRange): void {
        this.removeAppFilters();
        /* Assign the values (not by reference) and update filters */
        this.mssListFilters.copyDate = <DateRange> {
            from: copyDate.from,
            to: copyDate.to,
            calendar: copyDate.calendar
        };
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addCopyist(copyist: NameElement): void {
        this.removeAppFilters();
        this.mssListFilters.copyist = copyist;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addCopyPlace(copyPlace: VocTerm): void {
        this.removeAppFilters();
        this.mssListFilters.copyPlace = copyPlace;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addPatron(patron: NameElement): void {
        this.removeAppFilters();
        this.mssListFilters.patron = patron;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addOwner(owner: NameElement): void {
        this.removeAppFilters();
        this.mssListFilters.owner = owner;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    addSifa(sifaSelection: SifaSelection): void {
        this.removeAppFilters();
        this.mssListFilters.sifaSelection = sifaSelection;
        this.manuscriptsService.updateFilters(this.mssListFilters);
    }

    sortCopyists(): void {
        this.subscriptions.push(
            this.namelistsService.getCopyists(this.copyistFilters).subscribe(
                response => this.copyists = response.filter(
                    name => name.__countMss > 0 && (!this.copyistFilters.orderBy || name[this.copyistFilters.orderBy])
                ),
                err => console.error(err)
            )
        );
    }

    sortPatrons(): void {
        this.subscriptions.push(
            this.namelistsService.getPatrons(this.patronFilters).subscribe(
                response => this.patrons = response.filter(
                    name => name.__countMss > 0 && (!this.patronFilters.orderBy || name[this.patronFilters.orderBy])
                ),
                err => console.error(err)
            )
        );
    }

    sortOwners(): void {
        this.subscriptions.push(
            this.namelistsService.getOwners(this.ownerFilters).subscribe(
                response => this.owners = response.filter(
                    name => name.__countMss > 0 && (!this.ownerFilters.orderBy || name[this.ownerFilters.orderBy])
                ),
                err => console.error(err)
            )
        );
    }

    /* Remove all the filters activated by the application and not by the user */
    private removeAppFilters(): void {
        this.mssListFilters.timePeriod = null;
    }

    /* Assign the expanded visibility to a filter element */
    private expandFilterElement(filterElement: ElementRef): void {
        filterElement.nativeElement.className = 'collapse in';
    }

    /* Assign the collapsed visibility to a filter element */
    private collapseFilterElement(filterElement: ElementRef): void {
        filterElement.nativeElement.className = 'collapse';
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/components/ui/mss-filters/mss-filters.component.ts